import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2, Shield, MapPin, Camera, Target, CheckCircle,
  ChevronRight, ChevronLeft, Zap, Upload, X, Check, Star,
  Globe, Phone, Users, Briefcase, BookOpen, Calendar,
  TrendingUp, Video, ArrowRight, Info, Sun, Moon,
  FileText, Cloud, Eye, EyeOff, RefreshCw, Rocket, LogIn, UserPlus,
  Megaphone, ShoppingBag, GraduationCap, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { useTheme } from '@/context/ThemeContext';
import { useSTNAuth } from '@/context/STNAuthContext';
import * as stnApi from '@/api/stnApi';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// ── Constants ─────────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, icon: Building2, title: 'Company Basics',  desc: 'Tell us about your startup' },
  { id: 2, icon: Shield,    title: 'Legal Details',   desc: 'CIN, GST & PAN verification' },
  { id: 3, icon: MapPin,    title: 'Address',         desc: 'Registered & operational address' },
  { id: 4, icon: Camera,    title: 'Premises Docs',   desc: 'Photos & video of your workspace' },
  { id: 5, icon: Target,    title: 'Ecosystem Goals', desc: 'What do you want from us?' },
  { id: 6, icon: CheckCircle, title: 'Review & Submit', desc: 'Final review before going live' },
];

const COMPANY_TYPES = ['Private Limited (Pvt Ltd)', 'Limited Liability Partnership (LLP)', 'Sole Proprietorship', 'One Person Company (OPC)', 'Public Limited', 'Partnership Firm'];
const SECTORS = ['AI/ML & Deep Tech', 'FinTech', 'EdTech', 'AgriTech', 'HealthTech', 'CleanTech', 'E-commerce', 'SaaS / B2B Software', 'Consumer Tech', 'Logistics & Supply Chain', 'EV & Mobility', 'Media & Entertainment'];
const STAGES = ['Idea Stage', 'Pre-Seed', 'Seed', 'Series A', 'Series B+', 'Revenue Generating (Bootstrapped)'];
const TEAM_SIZES = ['1-5', '6-15', '16-50', '51-100', '100+'];
const FOUNDER_ROLES = ['Founder', 'Co-Founder', 'CEO', 'CTO', 'COO', 'CFO', 'Other'];
const INDIA_STATES = ['Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Delhi', 'Gujarat', 'Goa', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'];

const GOALS = [
  { id: 'intern',    icon: Users,      label: 'Hire Interns',       desc: 'Access AI-matched interns' },
  { id: 'fulltime',  icon: Briefcase,  label: 'Full-time Hiring',   desc: 'Recruit permanent talent' },
  { id: 'upskill',   icon: BookOpen,   label: 'Team Upskilling',    desc: 'PromptNexus AI training' },
  { id: 'events',    icon: Calendar,   label: 'Host Events',        desc: 'Hackathons & workshops' },
  { id: 'investors', icon: TrendingUp, label: 'Find Investors',     desc: 'Connect with VCs & angels' },
  { id: 'labs',      icon: Zap,        label: 'Lab Access',         desc: 'Book research & testing labs' },
  { id: 'mentor',    icon: Star,       label: 'Get Mentored',       desc: 'Expert startup mentorship' },
  { id: 'bihar',     icon: MapPin,     label: 'Bihar Talent',       desc: 'Local talent pipeline' },
  { id: 'webinars',  icon: Video,      label: 'Host Webinars',      desc: 'Run webinars with your own mentors' },
  { id: 'marketing', icon: Megaphone,  label: 'Marketing Pipeline', desc: 'Campaigns, leads & outreach support' },
  { id: 'ecommerce', icon: ShoppingBag,label: 'List Products',      desc: 'Sell or showcase products, with or without checkout' },
  { id: 'cohort',    icon: GraduationCap, label: 'Cohort Hiring',   desc: 'Host a hiring cohort/batch' },
];

const slideVariants = {
  enter:  (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1, transition: { duration: 0.35, ease: 'easeOut' } },
  exit:   (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0, transition: { duration: 0.25 } }),
};

// ── Sub-components ────────────────────────────────────────────────────────────

const VerifyField = ({ label, value, onChange, placeholder, hint, verified, onVerify, verifying, maxLength }) => (
  <div>
    <Label className="text-sm font-medium text-foreground">{label}</Label>
    {hint && <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">{hint}</p>}
    <div className="flex gap-2 mt-1">
      <div className="relative flex-1">
        <Input
          value={value} onChange={onChange} placeholder={placeholder}
          maxLength={maxLength} disabled={verified}
          className={cn('pr-9 font-mono text-sm', verified ? 'border-success focus-visible:ring-success/30 disabled:opacity-100' : '')}
        />
        {verified && <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-success" />}
      </div>
      <Button
        type="button" variant="outline" size="sm"
        className={cn('px-4 border-border', verified ? 'text-success border-success/30' : '')}
        onClick={onVerify} disabled={!value || verified || verifying}
      >
        {verifying ? (
          <span className="flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Checking…</span>
        ) : verified ? 'Verified ✓' : 'Verify'}
      </Button>
    </div>
  </div>
);

const PhotoUpload = ({ photos, setPhotos }) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const photoList = Array.isArray(photos) ? photos : [];

  const addFiles = async (files) => {
    const imgs = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (!imgs.length) return;

    // Add placeholders immediately so the user sees feedback
    const placeholders = imgs.map(f => ({
      id: `ph-${Math.random()}`,
      name: f.name,
      url: URL.createObjectURL(f),  // local preview only — not saved
      uploading: true,
      error: false,
    }));
    setPhotos([...photoList, ...placeholders]);

    // Upload each file to S3 and replace placeholder with real URL
    const results = await Promise.allSettled(
      imgs.map((f, i) =>
        stnApi.uploadPhoto(f).then(res => ({
          id: placeholders[i].id,
          name: f.name,
          url: res.url || res,
          key: res.key || '',
          uploading: false,
          error: false,
        }))
      )
    );

    setPhotos(prev => {
      const updated = Array.isArray(prev) ? [...prev] : [];
      results.forEach((result, i) => {
        const idx = updated.findIndex(p => p.id === placeholders[i].id);
        if (idx === -1) return;
        if (result.status === 'fulfilled') {
          updated[idx] = result.value;
        } else {
          updated[idx] = { ...updated[idx], uploading: false, error: true };
          toast.error(`Failed to upload ${imgs[i].name}`);
        }
      });
      return updated;
    });
  };

  return (
    <div className="space-y-3">
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={cn('border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all',
          dragging ? 'border-primary bg-primary/8 scale-[1.01]' : 'border-border hover:border-primary/40 hover:bg-muted/40'
        )}
      >
        <input ref={inputRef} type="file" multiple accept="image/*" className="hidden"
          onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
        <Cloud className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm font-medium text-foreground mb-1">
          Drag photos here or <span className="text-primary">click to upload</span>
        </p>
        <p className="text-xs text-muted-foreground">JPG, PNG, WEBP up to 8MB each · Min 3 photos recommended</p>
      </div>

      {photoList.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {photoList.map(p => (
            <div key={p.id} className="relative group aspect-square rounded-xl overflow-hidden border border-border bg-muted">
              {/* Preview image — local blob while uploading, real S3 URL once done */}
              <img src={p.url} alt={p.name} className="w-full h-full object-cover" />

              {/* Uploading overlay */}
              {p.uploading && (
                <div className="absolute inset-0 bg-background/70 flex flex-col items-center justify-center gap-1.5">
                  <RefreshCw className="w-5 h-5 text-primary animate-spin" />
                  <span className="text-[10px] text-muted-foreground">Uploading…</span>
                </div>
              )}

              {/* Error overlay */}
              {p.error && (
                <div className="absolute inset-0 bg-destructive/20 flex flex-col items-center justify-center gap-1">
                  <X className="w-5 h-5 text-destructive" />
                  <span className="text-[10px] text-destructive">Failed</span>
                </div>
              )}

              {/* Remove button — shown on hover for successfully uploaded */}
              {!p.uploading && (
                <div className="absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <button
                    onClick={e => { e.stopPropagation(); setPhotos(photoList.filter(x => x.id !== p.id)); }}
                    className="w-8 h-8 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {/* Uploaded badge */}
              {!p.uploading && !p.error && (
                <div className="absolute bottom-0 left-0 right-0 text-center py-1 text-xs text-primary-foreground bg-primary/80">
                  <Check className="w-3 h-3 inline mr-1" />Uploaded
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DocUpload = ({ label, docType, url, onUploaded, hint, required = false }) => {
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName]   = useState('');
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setUploading(true);
    try {
      const result = await stnApi.uploadDoc(file, docType);
      onUploaded(result.url || result);
      toast.success(`${label} uploaded successfully!`);
    } catch (err) {
      toast.error(err.message || `Failed to upload ${label}`);
      setFileName('');
    } finally {
      setUploading(false);
      // Reset input so same file can be re-selected
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const displayName = url
    ? (fileName || url.split('/').pop().replace(/^\d+-/, '') || 'Uploaded file')
    : fileName;

  return (
    <div>
      <Label className="text-sm font-medium text-foreground">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {hint && <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">{hint}</p>}
      <div
        onClick={() => !uploading && inputRef.current?.click()}
        className={cn(
          'mt-1.5 flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 border-dashed cursor-pointer transition-all select-none',
          url  ? 'border-success/40 bg-success/5 hover:bg-success/8'
               : 'border-border hover:border-primary/50 hover:bg-muted/30',
          uploading && 'pointer-events-none opacity-60'
        )}
      >
        <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={handleFile} />
        {uploading ? (
          <>
            <RefreshCw className="w-4 h-4 text-primary animate-spin flex-shrink-0" />
            <span className="text-sm text-muted-foreground">Uploading…</span>
          </>
        ) : url ? (
          <>
            <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
            <span className="text-sm text-foreground truncate flex-1">{displayName}</span>
            <span className="text-xs text-success font-medium flex-shrink-0">Uploaded ✓</span>
          </>
        ) : (
          <>
            <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <span className="text-sm text-muted-foreground flex-1">Click to upload</span>
            <span className="text-xs text-muted-foreground flex-shrink-0">PDF, JPG, PNG</span>
          </>
        )}
      </div>
    </div>
  );
};

const MAX_VIDEO_BYTES = 50 * 1024 * 1024;

const VideoUpload = ({ url, onUploaded, onRemoved }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_VIDEO_BYTES) {
      toast.error('Video must be 50MB or smaller');
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setUploading(true);
    try {
      const result = await stnApi.uploadVideo(file);
      onUploaded(result.url || result);
      toast.success('Walkthrough video uploaded successfully!');
    } catch (err) {
      toast.error(err.message || 'Failed to upload video');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  if (url) {
    return (
      <div className="border-2 border-success/40 bg-success/5 rounded-2xl p-4 flex items-center gap-3">
        <video src={url} className="w-24 h-16 rounded-lg object-cover bg-muted flex-shrink-0" controls />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-success flex-shrink-0" /> Walkthrough video uploaded
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">Replace or remove below</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <input ref={inputRef} type="file" accept=".mp4,.mov,video/mp4,video/quicktime" className="hidden" onChange={handleFile} />
          <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => inputRef.current?.click()}>
            {uploading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : 'Replace'}
          </Button>
          <button type="button" onClick={onRemoved} className="w-8 h-8 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive flex items-center justify-center flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => !uploading && inputRef.current?.click()}
      className={cn(
        'border-2 border-dashed border-border rounded-2xl p-6 text-center hover:border-primary/40 transition-colors group cursor-pointer',
        uploading && 'pointer-events-none opacity-60'
      )}
    >
      <input ref={inputRef} type="file" accept=".mp4,.mov,video/mp4,video/quicktime" className="hidden" onChange={handleFile} />
      {uploading ? (
        <>
          <RefreshCw className="w-8 h-8 text-primary mx-auto mb-2 animate-spin" />
          <p className="text-sm font-medium">Uploading…</p>
        </>
      ) : (
        <>
          <Video className="w-8 h-8 text-muted-foreground mx-auto mb-2 group-hover:text-primary transition-colors" />
          <p className="text-sm font-medium">Upload walkthrough video</p>
          <p className="text-xs text-muted-foreground mt-1">MP4, MOV up to 50MB</p>
          <Button type="button" variant="outline" size="sm" className="mt-3" onClick={e => { e.stopPropagation(); inputRef.current?.click(); }}>
            <Upload className="w-3.5 h-3.5 mr-2" /> Browse Video
          </Button>
        </>
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

export default function Onboarding() {
  const navigate  = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { isAuthenticated, founder, startup, register, login, refreshStartup } = useSTNAuth();

  // step: 0=welcome, 'auth'=register/login, 1-6=steps
  // Authenticated users skip the welcome splash and go directly to their saved step
  const [step, setStep]   = useState(() => {
    if (isAuthenticated && startup) {
      return startup.onboardingStep > 0 ? Math.min(startup.onboardingStep, 6) : 1;
    }
    return 0;
  });
  const [dir,  setDir]    = useState(1);
  const [authMode, setAuthMode] = useState('register'); // 'register' | 'login'
  const [submitting, setSubmitting] = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [verifying,  setVerifying]  = useState({ cin: false, gst: false, pan: false });

  // Terms of Service + Privacy Policy — required to register. Fetched live so
  // the checkbox always shows the currently published document.
  const [signupConsents, setSignupConsents] = useState([]);
  const [signupConsentsChecked, setSignupConsentsChecked] = useState({});
  const [signupConsentsLoading, setSignupConsentsLoading] = useState(true);
  useEffect(() => {
    stnApi.getSignupConsents('email')
      .then(r => setSignupConsents(r?.consents || []))
      .catch(() => setSignupConsents([]))
      .finally(() => setSignupConsentsLoading(false));
  }, []);
  const allSignupConsentsChecked = signupConsents.length > 0 && signupConsents.every(c => signupConsentsChecked[c.code]);

  // Company-verification consent — the document shown before "Submit Profile"
  // at the end of onboarding. Needs the founder's token, so fetched once
  // authenticated rather than at mount.
  const [onboardingConsent, setOnboardingConsent] = useState(null);
  const [onboardingConsentChecked, setOnboardingConsentChecked] = useState(false);
  const [onboardingConsentLoading, setOnboardingConsentLoading] = useState(true);
  useEffect(() => {
    if (!isAuthenticated) return;
    stnApi.getOnboardingConsent()
      .then(doc => setOnboardingConsent(doc))
      .catch(() => setOnboardingConsent(null))
      .finally(() => setOnboardingConsentLoading(false));
  }, [isAuthenticated]);

  // Auth form state
  const [authForm, setAuthForm] = useState({ name: '', email: '', phone: '', password: '', role: 'Founder' });
  const [showPwd, setShowPwd]   = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  // Onboarding form state — prefill from existing startup if resuming
  const [data, setData] = useState(() => {
    const s = startup;
    return {
      companyName: s?.companyName || '',
      companyType: s?.companyType || '',
      founded:     s?.founded     || '',
      website:     s?.website     || '',
      sector:      s?.sector      || '',
      stage:       s?.stage       || '',
      teamSize:    s?.teamSize    || '',
      cin:   s?.cin   || '',
      gst:   s?.gst   || '',
      pan:   s?.pan   || '',
      dpiit: s?.dpiit || '',
      verified: {
        cin: s?.verification?.cin?.verified || false,
        gst: s?.verification?.gst?.verified || false,
        pan: s?.verification?.pan?.verified || false,
      },
      gstCertUrl:  s?.gstCertUrl  || '',
      coiFileUrl:  s?.coiFileUrl  || '',
      dippCertUrl: s?.dippCertUrl || '',
      regLine1: s?.registeredAddress?.line1 || '',
      regLine2: s?.registeredAddress?.line2 || '',
      regCity:  s?.registeredAddress?.city  || '',
      regState: s?.registeredAddress?.state || '',
      regPin:   s?.registeredAddress?.pincode || '',
      sameAddress: s?.sameAddress || false,
      opLine1: s?.operationalAddress?.line1 || '',
      opLine2: s?.operationalAddress?.line2 || '',
      opCity:  s?.operationalAddress?.city  || '',
      opState: s?.operationalAddress?.state || '',
      opPin:   s?.operationalAddress?.pincode || '',
      photos:       Array.isArray(s?.photos) ? s.photos : [],
      videoUrl:     s?.videoUrl || '',
      videoDesc:    s?.premisesDescription || '',
      goals:        Array.isArray(s?.goals) ? s.goals : [],
      talentPipelineStates:    Array.isArray(s?.talentPipelineStates)    ? s.talentPipelineStates    : [],
      talentPipelineLocations: Array.isArray(s?.talentPipelineLocations) ? s.talentPipelineLocations : [],
      wantsOwnPaymentGateway:  s?.wantsOwnPaymentGateway  || false,
    };
  });

  const set = (key, val) => setData(p => ({ ...p, [key]: val }));

  // "Other" sector — data.sector always holds the final string sent to the
  // backend (a preset name or the typed custom one); this only tracks which
  // input to show. Initializes to true when resuming a startup whose saved
  // sector isn't one of the presets (an existing custom value).
  const [sectorOtherMode, setSectorOtherMode] = useState(() => !!data.sector && !SECTORS.includes(data.sector));

  // ── Talent pipeline location picker (Bihar Talent goal) ─────────────────────
  const [pinInput, setPinInput]   = useState('');
  const [pinLookingUp, setPinLookingUp] = useState(false);

  const toggleTalentState = (stateName) =>
    setData(p => ({
      ...p,
      talentPipelineStates: p.talentPipelineStates.includes(stateName)
        ? p.talentPipelineStates.filter(s => s !== stateName)
        : [...p.talentPipelineStates, stateName],
    }));

  const addTalentLocationByPincode = async () => {
    const pin = pinInput.trim();
    if (!/^\d{6}$/.test(pin)) { toast.error('Enter a valid 6-digit PIN code'); return; }
    if (data.talentPipelineLocations.some(l => l.pincode === pin)) { toast.error('Already added'); return; }
    setPinLookingUp(true);
    try {
      const res = await stnApi.resolvePincode(pin);
      if (res?.data?.success) {
        setData(p => ({
          ...p,
          talentPipelineLocations: [...p.talentPipelineLocations, { pincode: pin, city: res.data.city, state: res.data.state }],
        }));
        setPinInput('');
      } else {
        toast.error('Could not find that PIN code — double-check it');
      }
    } catch (e) {
      toast.error(e.message || 'Could not look up that PIN code');
    } finally {
      setPinLookingUp(false);
    }
  };

  const removeTalentLocation = (pin) =>
    setData(p => ({ ...p, talentPipelineLocations: p.talentPipelineLocations.filter(l => l.pincode !== pin) }));

  // ── Auth submission ────────────────────────────────────────────────────────

  const handleAuth = async (e) => {
    e.preventDefault();
    if (!authForm.email || !authForm.password) { toast.error('Email and password are required'); return; }
    if (authMode === 'register' && !authForm.name) { toast.error('Name is required'); return; }
    if (authMode === 'register' && !allSignupConsentsChecked) {
      toast.error('Please agree to the Terms of Service and Privacy Policy to continue');
      return;
    }
    setAuthLoading(true);
    try {
      if (authMode === 'register') {
        await register({
          name: authForm.name, email: authForm.email, phone: authForm.phone, password: authForm.password, role: authForm.role,
          consents: signupConsents.map(c => ({ code: c.code, uniqueId: c.uniqueId, accepted: true })),
        });
        toast.success('Account created! Let\'s set up your startup.');
      } else {
        await login({ email: authForm.email, password: authForm.password });
        toast.success('Welcome back!');
      }
      setDir(1); setStep(1);
    } catch (err) {
      toast.error(err.message || 'Authentication failed');
    } finally {
      setAuthLoading(false);
    }
  };

  // ── Verification ───────────────────────────────────────────────────────────

  const startVerify = async (field) => {
    if (!data[field]) return;
    setVerifying(p => ({ ...p, [field]: true }));
    try {
      let result;
      if (field === 'cin') result = await stnApi.verifyCIN(data.cin, data.companyName);
      if (field === 'gst') result = await stnApi.verifyGST(data.gst);
      if (field === 'pan') result = await stnApi.verifyPAN(data.pan);

      if (result.verified) {
        setData(p => ({ ...p, verified: { ...p.verified, [field]: true } }));
        const provider = result.provider === 'sandbox' ? 'Real-time KYC' : result.provider === 'gstinapi' ? 'GSTN records' : result.provider === 'opencorporates' ? 'OpenCorporates' : 'Format';
        toast.success(`${field.toUpperCase()} verified! (${provider})`);
        const officialName = result.data?.name?.trim();
        if (officialName && officialName.toLowerCase() !== data.companyName.trim().toLowerCase()) {
          if (!data.companyName.trim()) {
            // Nothing typed yet (the common case now that GST verification
            // happens first, in Company Basics) — just fill it in directly.
            set('companyName', officialName);
          } else {
            // Already has a value — offer, don't silently overwrite, since a
            // startup may intentionally use a trade/brand name that differs
            // from its officially registered one.
            toast(`We found "${officialName}" as the registered company name.`, {
              action: { label: 'Use this', onClick: () => set('companyName', officialName) },
              cancel: { label: 'Keep mine', onClick: () => {} },
              duration: 10000,
            });
          }
        }
      } else {
        toast.error(result.error || `${field.toUpperCase()} verification failed`);
      }
    } catch (err) {
      toast.error(err.message || `${field.toUpperCase()} verification failed`);
    } finally {
      setVerifying(p => ({ ...p, [field]: false }));
    }
  };

  // ── Step autosave ──────────────────────────────────────────────────────────

  const autosave = async (stepNum, payload) => {
    if (!isAuthenticated) return;
    setSaving(true);
    try {
      await stnApi.saveStep(stepNum, payload);
    } catch (_) {
      // silent autosave failure — don't block UX
    } finally {
      setSaving(false);
    }
  };

  // ── Navigation ─────────────────────────────────────────────────────────────

  const next = async () => {
    if (step === 1 && !data.companyName) { toast.error('Company name is required'); return; }
    if (step === 2 && !data.cin)         { toast.error('Please enter your CIN number'); return; }
    if (step === 3 && !data.regLine1)    { toast.error('Registered address line 1 is required'); return; }
    if (step === 5 && !data.goals.length){ toast.error('Select at least one goal to continue'); return; }

    // Autosave current step
    const stepPayloads = {
      1: { companyName: data.companyName, companyType: data.companyType, founded: data.founded, website: data.website, sector: data.sector, stage: data.stage, teamSize: data.teamSize },
      2: { cin: data.cin, gst: data.gst, pan: data.pan, dpiit: data.dpiit, gstCertUrl: data.gstCertUrl, coiFileUrl: data.coiFileUrl, dippCertUrl: data.dippCertUrl },
      3: { registeredAddress: { line1: data.regLine1, line2: data.regLine2, city: data.regCity, state: data.regState, pincode: data.regPin }, operationalAddress: { line1: data.opLine1, line2: data.opLine2, city: data.opCity, state: data.opState, pincode: data.opPin }, sameAddress: data.sameAddress },
      4: { photos: data.photos.map(p => ({ url: p.url, name: p.name, key: p.key || '' })), videoUrl: data.videoUrl, premisesDescription: data.videoDesc },
      5: { goals: data.goals, talentPipelineStates: data.talentPipelineStates, talentPipelineLocations: data.talentPipelineLocations, wantsOwnPaymentGateway: data.wantsOwnPaymentGateway },
    };
    if (stepPayloads[step]) await autosave(step, stepPayloads[step]);

    setDir(1); setStep(s => s + 1);
  };

  const back = () => { setDir(-1); setStep(s => s - 1); };

  const toggleGoal = (id) => set('goals', data.goals.includes(id) ? data.goals.filter(g => g !== id) : [...data.goals, id]);

  // ── Final submit ───────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!data.goals.length) {
      toast.error('Select at least one Ecosystem Goal before submitting');
      return;
    }
    if (!onboardingConsentChecked) {
      toast.error('Please agree to company verification before submitting');
      return;
    }
    setSubmitting(true);
    try {
      await stnApi.saveStep(5, { goals: data.goals, talentPipelineStates: data.talentPipelineStates, talentPipelineLocations: data.talentPipelineLocations, wantsOwnPaymentGateway: data.wantsOwnPaymentGateway });
      await stnApi.submitProfile(
        onboardingConsent ? { accepted: true, uniqueId: onboardingConsent.uniqueId } : undefined,
      );
      await refreshStartup();
      toast.success('Profile submitted! We\'ll review and verify within 24 hours.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const progress = step === 0 || step === 'auth' ? 0 : Math.round((step / 6) * 100);

  // ── Step renderers ─────────────────────────────────────────────────────────

  const renderAuthStep = () => (
    <div className="max-w-md mx-auto w-full">
      <div className="flex gap-1 p-1 bg-muted rounded-xl mb-6">
        {['register', 'login'].map(mode => (
          <button key={mode} onClick={() => setAuthMode(mode)}
            className={cn('flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all',
              authMode === mode ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            )}>
            {mode === 'register' ? <><UserPlus className="w-3.5 h-3.5" /> Create Account</> : <><LogIn className="w-3.5 h-3.5" /> Sign In</>}
          </button>
        ))}
      </div>
      <form onSubmit={handleAuth} className="space-y-4">
        {authMode === 'register' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Full Name *</Label>
                <Input className="mt-1.5" placeholder="Vikram Agarwal" value={authForm.name}
                  onChange={e => setAuthForm(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input className="mt-1.5" placeholder="+91 98765 43210" type="tel" value={authForm.phone}
                  onChange={e => setAuthForm(p => ({ ...p, phone: e.target.value }))} />
              </div>
              <div>
                <Label>Your Role</Label>
                <Select value={authForm.role} onValueChange={v => setAuthForm(p => ({ ...p, role: v }))}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{FOUNDER_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </>
        )}
        <div>
          <Label>Email Address *</Label>
          <Input className="mt-1.5" type="email" placeholder="you@startup.com" value={authForm.email}
            onChange={e => setAuthForm(p => ({ ...p, email: e.target.value }))} />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label>Password *</Label>
            {authMode !== 'register' && (
              <button type="button" onClick={() => navigate('/forgot-password')}
                className="text-xs text-primary hover:underline font-medium">
                Forgot password?
              </button>
            )}
          </div>
          <div className="relative mt-1.5">
            <Input type={showPwd ? 'text' : 'password'} placeholder="Min 8 characters" value={authForm.password}
              className="pr-10" onChange={e => setAuthForm(p => ({ ...p, password: e.target.value }))} />
            <button type="button" onClick={() => setShowPwd(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {authMode === 'register' && (
          <div className="space-y-2">
            {signupConsentsLoading ? (
              <p className="text-xs text-muted-foreground">Loading terms…</p>
            ) : signupConsents.length === 0 ? (
              <p className="text-xs text-destructive">Could not load Terms of Service / Privacy Policy — please refresh and try again.</p>
            ) : (
              signupConsents.map(c => (
                <label key={c.code} className="flex cursor-pointer gap-2.5 rounded-lg border border-border bg-muted/30 p-2.5 hover:border-primary/40 transition-colors">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                    checked={!!signupConsentsChecked[c.code]}
                    onChange={e => setSignupConsentsChecked(s => ({ ...s, [c.code]: e.target.checked }))}
                  />
                  <span className="min-w-0">
                    <span className="block text-xs font-semibold text-foreground">I agree to the {c.title}</span>
                    <span className="mt-0.5 block text-[10.5px] leading-relaxed text-muted-foreground">{c.description}</span>
                  </span>
                </label>
              ))
            )}
          </div>
        )}
        <Button
          type="submit"
          className="w-full gradient-primary text-primary-foreground border-0 h-11 font-semibold"
          disabled={authLoading || (authMode === 'register' && !allSignupConsentsChecked)}
        >
          {authLoading
            ? <span className="flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" />Please wait…</span>
            : authMode === 'register' ? 'Create Account & Continue' : 'Sign In & Continue'
          }
        </Button>
      </form>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 1: return (
        <div className="space-y-5">
          <div className="p-3.5 rounded-xl bg-info/8 border border-info/20 flex gap-3">
            <Info className="w-4 h-4 text-info flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">Have a GSTIN already? Verify it first and we'll fill in your registered company name automatically. Don't have one yet? Skip ahead and fill in your details manually below.</p>
          </div>
          <VerifyField
            label="GST Identification Number (GSTIN)"
            hint="15-char format: 27AABCU9603R1ZX · Optional — verifying here auto-fills Company Name below"
            placeholder="27AABCU9603R1ZX"
            value={data.gst} onChange={e => set('gst', e.target.value.toUpperCase())}
            verified={data.verified.gst} onVerify={() => startVerify('gst')} verifying={verifying.gst} maxLength={15}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Company / Startup Name <span className="text-destructive">*</span></Label>
              {data.verified.gst ? (
                <div className="mt-1.5 flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-success/8 border border-success/25">
                  <Check className="w-4 h-4 text-success flex-shrink-0" />
                  <span className="text-sm font-medium text-foreground">{data.companyName}</span>
                  <span className="text-[11px] text-success ml-auto flex-shrink-0">Verified via GST</span>
                </div>
              ) : (
                <Input className="mt-1.5" placeholder="e.g. TechVision AI Pvt Ltd" value={data.companyName} onChange={e => set('companyName', e.target.value)} />
              )}
            </div>
            <div>
              <Label>Company Type</Label>
              <Select value={data.companyType} onValueChange={v => set('companyType', v)}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{COMPANY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Year Founded</Label>
              <Input className="mt-1.5" placeholder="2022" type="number" min="2000" max="2026" value={data.founded} onChange={e => set('founded', e.target.value)} />
            </div>
            <div>
              <Label>Primary Sector</Label>
              <Select
                value={sectorOtherMode ? 'Other' : data.sector}
                onValueChange={v => {
                  if (v === 'Other') { setSectorOtherMode(true); set('sector', ''); }
                  else { setSectorOtherMode(false); set('sector', v); }
                }}
              >
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select sector" /></SelectTrigger>
                <SelectContent>
                  {SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              {sectorOtherMode && (
                <Input
                  className="mt-2"
                  placeholder="Enter your sector"
                  value={data.sector}
                  onChange={e => set('sector', e.target.value)}
                  autoFocus
                />
              )}
            </div>
            <div>
              <Label>Startup Stage</Label>
              <Select value={data.stage} onValueChange={v => set('stage', v)}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select stage" /></SelectTrigger>
                <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Team Size</Label>
              <Select value={data.teamSize} onValueChange={v => set('teamSize', v)}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select size" /></SelectTrigger>
                <SelectContent>{TEAM_SIZES.map(s => <SelectItem key={s} value={s}>{s} employees</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Website <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input className="mt-1.5" placeholder="https://yourstartup.com" value={data.website} onChange={e => set('website', e.target.value)} />
            </div>
          </div>
        </div>
      );

      case 2: return (
        <div className="space-y-5">
          <div className="p-3.5 rounded-xl bg-info/8 border border-info/20 flex gap-3">
            <Info className="w-4 h-4 text-info flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">We verify credentials using format validation + OpenCorporates (free) + sandbox.co.in KYC when configured. Verified startups get 3× more interns.</p>
          </div>
          <VerifyField
            label="Corporate Identity Number (CIN) *"
            hint="21-char format: U72200DL2020PTC123456"
            placeholder="U72200DL2020PTC123456"
            value={data.cin} onChange={e => set('cin', e.target.value.toUpperCase())}
            verified={data.verified.cin} onVerify={() => startVerify('cin')} verifying={verifying.cin} maxLength={21}
          />
          <VerifyField
            label="GST Identification Number (GSTIN)"
            hint="15-char format: 27AABCU9603R1ZX · Optional — don't have a GSTIN yet? Leave this blank and continue; you can share it with our team anytime afterward to get added."
            placeholder="27AABCU9603R1ZX"
            value={data.gst} onChange={e => set('gst', e.target.value.toUpperCase())}
            verified={data.verified.gst} onVerify={() => startVerify('gst')} verifying={verifying.gst} maxLength={15}
          />
          <VerifyField
            label="PAN Number"
            hint="10-char format: ABCDE1234F"
            placeholder="ABCDE1234F"
            value={data.pan} onChange={e => set('pan', e.target.value.toUpperCase())}
            verified={data.verified.pan} onVerify={() => startVerify('pan')} verifying={verifying.pan} maxLength={10}
          />
          <div>
            <Label>DPIIT Registration Number <span className="text-muted-foreground text-xs">(optional)</span></Label>
            <Input className="mt-1.5 font-mono" placeholder="DIPP12345" value={data.dpiit} onChange={e => set('dpiit', e.target.value)} />
          </div>

          <div className="border-t border-border pt-5 mt-2">
            <h3 className="text-sm font-semibold text-foreground mb-1 flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" /> Legal Documents
            </h3>
            <p className="text-xs text-muted-foreground mb-4">Upload scanned copies or PDFs. Files are securely stored and used only for verification.</p>
            <div className="space-y-4">
              <DocUpload
                label="GST Registration Certificate"
                docType="gstCert"
                url={data.gstCertUrl}
                onUploaded={url => set('gstCertUrl', url)}
                hint="Certificate issued by GSTN upon GST registration"
              />
              <DocUpload
                label="Certificate of Incorporation (COI)"
                docType="coi"
                url={data.coiFileUrl}
                onUploaded={url => set('coiFileUrl', url)}
                hint="Issued by MCA / Registrar of Companies at the time of incorporation"
                required
              />
              <DocUpload
                label="DIPP / Startup India Certificate"
                docType="dippCert"
                url={data.dippCertUrl}
                onUploaded={url => set('dippCertUrl', url)}
                hint="DPIIT recognition letter or Startup India certificate (if applicable)"
              />
            </div>
          </div>
        </div>
      );

      case 3: return (
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center text-xs font-bold text-primary">R</div>
              Registered Address
            </h3>
            <div className="space-y-3">
              <div><Label>Address Line 1 *</Label><Input className="mt-1.5" placeholder="Building name, street" value={data.regLine1} onChange={e => set('regLine1', e.target.value)} /></div>
              <div><Label>Address Line 2</Label><Input className="mt-1.5" placeholder="Area, locality" value={data.regLine2} onChange={e => set('regLine2', e.target.value)} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>City *</Label><Input className="mt-1.5" placeholder="City" value={data.regCity} onChange={e => set('regCity', e.target.value)} /></div>
                <div>
                  <Label>State *</Label>
                  <Select value={data.regState} onValueChange={v => set('regState', v)}>
                    <SelectTrigger className="mt-1.5"><SelectValue placeholder="State" /></SelectTrigger>
                    <SelectContent className="max-h-48">{INDIA_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Pincode *</Label><Input className="mt-1.5" placeholder="110001" maxLength={6} value={data.regPin} onChange={e => set('regPin', e.target.value.replace(/\D/g, '').slice(0, 6))} /></div>
              </div>
            </div>
          </div>
          <div className="border-t border-border pt-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-chart-3/15 flex items-center justify-center text-xs font-bold text-chart-3">O</div>
                Operational Address
              </h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={data.sameAddress} onChange={e => set('sameAddress', e.target.checked)} />
                <span className="text-xs text-muted-foreground">Same as registered</span>
              </label>
            </div>
            {!data.sameAddress && (
              <div className="space-y-3">
                <div><Label>Address Line 1</Label><Input className="mt-1.5" placeholder="Building name, street" value={data.opLine1} onChange={e => set('opLine1', e.target.value)} /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>City</Label><Input className="mt-1.5" value={data.opCity} onChange={e => set('opCity', e.target.value)} /></div>
                  <div>
                    <Label>State</Label>
                    <Select value={data.opState} onValueChange={v => set('opState', v)}>
                      <SelectTrigger className="mt-1.5"><SelectValue placeholder="State" /></SelectTrigger>
                      <SelectContent className="max-h-48">{INDIA_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Pincode</Label><Input className="mt-1.5" maxLength={6} value={data.opPin} onChange={e => set('opPin', e.target.value.replace(/\D/g, '').slice(0, 6))} /></div>
                </div>
              </div>
            )}
          </div>
        </div>
      );

      case 4: return (
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold mb-1">Office / Premises Photos *</h3>
            <p className="text-xs text-muted-foreground mb-3">Upload 3–10 clear photos: entrance, workstations, meeting rooms.</p>
            <PhotoUpload
              photos={data.photos}
              setPhotos={v => setData(p => ({ ...p, photos: typeof v === 'function' ? v(p.photos) : v }))}
            />
          </div>
          <div>
            <h3 className="text-sm font-semibold mb-1">Premises Walkthrough Video <span className="text-muted-foreground font-normal">(optional)</span></h3>
            <p className="text-xs text-muted-foreground mb-3">1–2 min video improves intern applications by 3×.</p>
            <VideoUpload
              url={data.videoUrl}
              onUploaded={url => set('videoUrl', url)}
              onRemoved={() => set('videoUrl', '')}
            />
          </div>
          <div>
            <Label>About Your Premises <span className="text-muted-foreground text-xs">(optional)</span></Label>
            <Textarea className="mt-1.5" rows={3} placeholder="Describe your office environment, facilities, culture…" value={data.videoDesc} onChange={e => set('videoDesc', e.target.value)} />
          </div>
        </div>
      );

      case 5: return (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Select all goals that apply. We'll tailor your dashboard accordingly.</p>
          <div className="grid grid-cols-2 gap-3">
            {GOALS.map(g => {
              const selected = data.goals.includes(g.id);
              return (
                <button key={g.id} onClick={() => toggleGoal(g.id)}
                  className={cn('flex items-start gap-3 p-4 rounded-xl border text-left transition-all',
                    selected ? 'border-primary bg-primary/8 shadow-sm' : 'border-border hover:border-primary/30 hover:bg-muted/50'
                  )}>
                  <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors',
                    selected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}>
                    <g.icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className={cn('text-sm font-medium', selected ? 'text-primary' : 'text-foreground')}>{g.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{g.desc}</p>
                  </div>
                  {selected && <Check className="w-4 h-4 text-primary ml-auto flex-shrink-0" />}
                </button>
              );
            })}
          </div>

          {data.goals.includes('bihar') && (
            <div className="p-4 rounded-xl border border-border bg-muted/30 space-y-4">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                <p className="text-sm font-semibold text-foreground">Where do you want talent from?</p>
              </div>

              <div>
                <Label className="mb-1.5 block text-xs">States (select all that apply)</Label>
                <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto p-2 rounded-lg border border-border bg-background">
                  {INDIA_STATES.map(s => {
                    const checked = data.talentPipelineStates.includes(s);
                    return (
                      <button key={s} type="button" onClick={() => toggleTalentState(s)}
                        className={cn('text-xs px-2.5 py-1 rounded-full border font-medium transition-colors',
                          checked ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/40')}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="mb-1.5 block text-xs">Specific locations (search by PIN code)</Label>
                <div className="flex gap-2">
                  <Input
                    value={pinInput}
                    onChange={e => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTalentLocationByPincode(); } }}
                    placeholder="6-digit PIN code"
                    className="font-mono text-sm"
                  />
                  <Button type="button" variant="outline" onClick={addTalentLocationByPincode} disabled={pinInput.length !== 6 || pinLookingUp}>
                    {pinLookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Add</>}
                  </Button>
                </div>
                {data.talentPipelineLocations.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {data.talentPipelineLocations.map(l => (
                      <span key={l.pincode} className="flex items-center gap-1.5 text-xs pl-2.5 pr-1.5 py-1 rounded-full bg-primary/8 border border-primary/25 text-foreground">
                        {l.city ? `${l.city}, ${l.state}` : l.pincode} <span className="text-muted-foreground font-mono">({l.pincode})</span>
                        <button type="button" onClick={() => removeTalentLocation(l.pincode)} className="text-muted-foreground hover:text-destructive">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {data.goals.includes('ecommerce') && (
            <label className="flex items-center gap-3 p-4 rounded-xl border border-border bg-muted/30 cursor-pointer">
              <input
                type="checkbox"
                checked={data.wantsOwnPaymentGateway}
                onChange={e => set('wantsOwnPaymentGateway', e.target.checked)}
                className="w-4 h-4 accent-primary"
              />
              <div>
                <p className="text-sm font-medium text-foreground">I'd like to use my own payment gateway</p>
                <p className="text-xs text-muted-foreground mt-0.5">Leave unchecked for an enquiry-only product listing with no checkout.</p>
              </div>
            </label>
          )}
        </div>
      );

      case 6: return (
        <div className="space-y-4">
          {[{
            icon: Building2, title: 'Company',
            value: data.companyName || 'Not filled',
            items: [data.companyType, data.sector, data.stage, data.teamSize && `${data.teamSize} employees`].filter(Boolean),
          }, {
            icon: Shield, title: 'Legal Verification',
            value: [data.verified.cin && 'CIN ✓', data.verified.gst && 'GST ✓', data.verified.pan && 'PAN ✓'].filter(Boolean).join(' · ') || 'None verified',
            items: [data.cin, data.gst, data.pan].filter(Boolean),
          }, {
            icon: MapPin, title: 'Address',
            value: [data.regCity, data.regState, data.regPin].filter(Boolean).join(', ') || 'Not filled',
            items: [data.regLine1].filter(Boolean),
          }, {
            icon: Camera, title: 'Premises Docs',
            value: `${data.photos.length} photos uploaded`,
            items: [],
          }, {
            icon: Target, title: 'Goals',
            value: `${data.goals.length} goals selected`,
            items: data.goals.map(id => GOALS.find(g => g.id === id)?.label).filter(Boolean),
          }].map((section) => (
            <div key={section.title} className="flex items-start gap-3 p-4 rounded-xl bg-muted/50 border border-border">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <section.icon className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{section.title}</span>
                  <button onClick={() => { setDir(-1); setStep(STEPS.findIndex(s => s.title.includes(section.title.split(' ')[0])) + 1); }}
                    className="text-xs text-primary hover:underline">Edit</button>
                </div>
                <p className="text-sm font-medium text-foreground mt-0.5">{section.value}</p>
                {section.items.length > 0 && <p className="text-xs text-muted-foreground mt-0.5">{section.items.slice(0, 3).join(' · ')}</p>}
              </div>
            </div>
          ))}
          <div className="p-3.5 rounded-xl bg-success/8 border border-success/20">
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-success">Almost there!</span> Profile reviewed within 24 hours.
            </p>
          </div>

          {/* Real click-to-accept, not passive text — the account was created
              with Terms + Privacy already accepted at registration; this is a
              second, more specific consent for handing over CIN/GST/PAN and
              the registered address for verification. */}
          <div className="space-y-2">
            {onboardingConsentLoading ? (
              <p className="text-xs text-muted-foreground">Loading consent terms…</p>
            ) : !onboardingConsent ? (
              <p className="text-xs text-destructive">Could not load the consent document — please refresh and try again.</p>
            ) : (
              <label className="flex cursor-pointer gap-3 rounded-xl border border-border bg-muted/30 p-3 hover:border-primary/40 transition-colors">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0 accent-primary"
                  checked={onboardingConsentChecked}
                  onChange={e => setOnboardingConsentChecked(e.target.checked)}
                />
                <span className="min-w-0">
                  <span className="block text-xs font-semibold text-foreground">I agree to {onboardingConsent.title}</span>
                  <span className="mt-1 block text-[11px] leading-relaxed text-muted-foreground">{onboardingConsent.description}</span>
                </span>
              </label>
            )}
          </div>
        </div>
      );
      default: return null;
    }
  };

  // ── Welcome screen ─────────────────────────────────────────────────────────

  if (step === 0) return (
    <div className="min-h-screen bg-background flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-10" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 20%, hsl(263 70% 50% / 0.5), transparent)' }} />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-xl px-6">
        <div className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center mx-auto mb-6">
          <Zap className="w-8 h-8 text-primary-foreground" />
        </div>
        <h1 className="text-4xl font-bold text-foreground mb-4">
          Let's set up your<br /><span className="gradient-text">startup profile</span>
        </h1>
        <p className="text-muted-foreground mb-8 leading-relaxed">
          Complete your onboarding in 6 easy steps. Takes about 5–7 minutes and unlocks the full StartupNexus ecosystem.
        </p>
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[['6 steps', 'Quick & easy'], ['5 minutes', 'Average time'], ['100% secure', 'Encrypted data']].map(([v, l]) => (
            <div key={v} className="p-3 rounded-xl bg-card border border-border">
              <div className="font-bold text-foreground">{v}</div>
              <div className="text-xs text-muted-foreground">{l}</div>
            </div>
          ))}
        </div>
        <Button size="lg" className="gradient-primary text-primary-foreground border-0 h-12 px-8 font-semibold"
          onClick={() => {
            if (isAuthenticated) { setDir(1); setStep(startup?.onboardingStep > 0 ? Math.min(startup.onboardingStep, 6) : 1); }
            else { setDir(1); setStep('auth'); }
          }}>
          {isAuthenticated ? 'Continue Onboarding' : 'Get Started'} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        {isAuthenticated && founder && (
          <p className="text-xs text-muted-foreground mt-3">Signed in as <strong>{founder.name}</strong> ({founder.email})</p>
        )}
      </motion.div>
      <button onClick={() => navigate('/')} className="absolute top-6 left-6 text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
        <ChevronLeft className="w-4 h-4" />Back to home
      </button>
      <button onClick={toggleTheme} className="absolute top-6 right-6 p-2 rounded-lg hover:bg-accent text-muted-foreground">
        {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>
    </div>
  );

  // ── Auth step ──────────────────────────────────────────────────────────────

  if (step === 'auth') return (
    <div className="min-h-screen flex bg-background">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col w-80 xl:w-96 flex-shrink-0 relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, hsl(263 70% 18%), hsl(244 40% 10%))' }}>
        <div className="relative z-10 flex flex-col h-full p-8">
          <div className="flex items-center gap-2.5 mb-10">
            <div className="w-8 h-8 rounded-lg bg-white/15 border border-white/25 flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-white text-sm">StartupNexus</span>
          </div>
          <div className="flex-1 flex flex-col justify-center">
            <h2 className="text-2xl font-bold text-white mb-3">{authMode === 'register' ? 'Join StartupNexus' : 'Welcome back'}</h2>
            <p className="text-sm text-white/65 mb-8 leading-relaxed">India's startup ecosystem platform — connecting founders with interns, mentors, investors, and labs.</p>
            {['AI-matched intern hiring', 'Company credential verification', 'Mentor & investor network', 'PromptNexus AI team training'].map(f => (
              <div key={f} className="flex items-center gap-2.5 mb-3">
                <Check className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-white/80">{f}</span>
              </div>
            ))}
          </div>
          <div className="p-4 rounded-xl bg-white/8 border border-white/15">
            <p className="text-xs text-white/60">Your data is encrypted and verified against MCA & GSTN databases.</p>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col">
        <div className="flex items-center justify-between px-6 lg:px-10 py-5 border-b border-border">
          <button onClick={() => { setDir(-1); setStep(0); }} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-accent text-muted-foreground">
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center px-6 lg:px-10 py-10">
          <AnimatePresence mode="wait">
            <motion.div key={authMode} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} className="w-full max-w-md">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-foreground">{authMode === 'register' ? 'Create your account' : 'Sign in'}</h2>
                <p className="text-sm text-muted-foreground mt-1">{authMode === 'register' ? 'Start your StartupNexus onboarding journey' : 'Continue where you left off'}</p>
              </div>
              {renderAuthStep()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );

  // ── Step layout ────────────────────────────────────────────────────────────

  const currentStepData = STEPS[step - 1];

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left Panel */}
      <div className="hidden lg:flex flex-col w-80 xl:w-96 flex-shrink-0 relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, hsl(263 70% 18%), hsl(244 40% 10%))' }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 120% 60% at 10% 80%, hsl(263 70% 30% / 0.3), transparent)' }} />
        <div className="relative z-10 flex flex-col h-full p-8">
          <div className="flex items-center gap-2.5 mb-10">
            <div className="w-8 h-8 rounded-lg bg-white/15 border border-white/25 flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-white text-sm">StartupNexus</span>
          </div>
          {founder && (
            <div className="mb-6 flex items-center gap-2.5 p-3 rounded-xl bg-white/8 border border-white/15">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {founder.name?.[0]?.toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-white truncate">{founder.name}</p>
                <p className="text-xs text-white/55 truncate">{founder.email}</p>
              </div>
            </div>
          )}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-white/60">Onboarding Progress</span>
              <span className="text-xs font-bold text-white">{progress}%</span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-white/15">
              <motion.div className="h-full rounded-full" style={{ background: 'hsl(var(--primary))' }}
                initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }} />
            </div>
          </div>
          <div className="space-y-1 flex-1">
            {STEPS.map((s, i) => {
              const idx = i + 1;
              const done = idx < step;
              const current = idx === step;
              return (
                <div key={s.id} className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <div className={cn('w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all',
                      done ? 'bg-emerald-500 text-white' : current ? 'bg-primary text-white' : 'bg-white/10 text-white/35')}>
                      {done ? <Check className="w-4 h-4" /> : <span className="text-xs font-bold">{idx}</span>}
                    </div>
                    {i < STEPS.length - 1 && <div className={cn('w-0.5 h-7 mt-1 rounded-full transition-colors', done ? 'bg-emerald-500/50' : 'bg-white/12')} />}
                  </div>
                  <div className={cn('pb-6', current ? 'opacity-100' : done ? 'opacity-70' : 'opacity-35')}>
                    <p className={cn('text-sm font-medium', current ? 'text-white' : 'text-white/75')}>{s.title}</p>
                    {current && <p className="text-xs text-white/55 mt-0.5">{s.desc}</p>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="p-4 rounded-xl bg-white/8 border border-white/15">
            <p className="text-xs text-white/65 leading-relaxed">
              <span className="font-semibold text-white">Tip:</span> Verified startups get <strong>3× more intern applications</strong> and appear first in partner search results.
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex flex-col min-h-screen">
        <div className="flex items-center justify-between px-6 lg:px-10 py-5 border-b border-border">
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">Step {step} of 6</div>
            <h2 className="text-lg font-bold text-foreground">{currentStepData?.title}</h2>
          </div>
          <div className="flex items-center gap-3">
            {saving && <span className="text-xs text-muted-foreground flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" />Saving…</span>}
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-accent text-muted-foreground">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => navigate('/dashboard')}>Save & Exit</Button>
          </div>
        </div>

        {/* Mobile progress */}
        <div className="lg:hidden px-6 pt-4">
          <Progress value={progress} className="h-1.5" />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>Step {step} of 6</span><span>{progress}%</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 lg:px-10 py-8">
          <div className="max-w-2xl">
            <AnimatePresence mode="wait" custom={dir}>
              <motion.div key={step} custom={dir} variants={slideVariants} initial="enter" animate="center" exit="exit">
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <div className="px-6 lg:px-10 py-5 border-t border-border flex items-center justify-between">
          <Button variant="ghost" onClick={step === 1 ? () => { setDir(-1); setStep('auth'); } : back} className="gap-2 text-muted-foreground">
            <ChevronLeft className="w-4 h-4" /> Back
          </Button>
          <div className="flex gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={cn('h-1.5 rounded-full transition-all',
                i + 1 === step ? 'w-6 bg-primary' : i + 1 < step ? 'w-1.5 bg-primary/50' : 'w-1.5 bg-border')} />
            ))}
          </div>
          {step < 6 ? (
            <Button onClick={next} className="gradient-primary text-primary-foreground border-0 gap-2">
              Continue <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting || !onboardingConsentChecked}
              className="gradient-primary text-primary-foreground border-0 gap-2">
              {submitting
                ? <><RefreshCw className="w-4 h-4 animate-spin" /> Submitting…</>
                : <><Rocket className="w-4 h-4" /> Submit Profile</>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
